import { post } from '@/services/http'
import { endpoints } from '@/services/endpoints'
import { ApiError, ERROR_CODES } from '@/services/apiError'

/**
 * Uploading a file.
 *
 * ── The bytes do not go through the API ──────────────────────────────────
 * The API issues a short-lived presigned address and the browser PUTs the file
 * straight to object storage. Three calls, and only the first and third are API
 * routes:
 *
 *   1. POST /files/upload-url   → { file_id, upload_url, method, headers }
 *   2. PUT  <upload_url>        → the bytes, direct to storage. Not this API.
 *   3. POST /files/{id}/confirm → the server fetches the object back and
 *                                 inspects it, then marks it confirmed.
 *
 * What comes back is a `file_id`, and it is not attached to anything yet. It
 * becomes an avatar, a logo or a document only when some other write is sent
 * carrying that id — at which point the server moves the object out of the temp
 * folder and marks it attached. So an upload that is never saved leaves a
 * harmless orphan rather than half a record.
 *
 * ── Step 2 must not use the app's axios instance ─────────────────────────
 * `http` attaches a Bearer token, sets JSON content headers, unwraps a response
 * envelope and refreshes the session on a 401. Object storage wants none of
 * that: the credential is baked into the URL's signature, the Content-Type must
 * be exactly the one the presign was issued for, and the response is not JSON.
 * A bare `fetch` is the correct transport, and this is the one place in the app
 * that reaches past the shared client on purpose.
 */

/** Purposes the API defines. Each has its own size cap and accepted types. */
export const FILE_PURPOSES = {
  accountLogo: 'account_logo',
  userAvatar: 'user_avatar',
  logbookPhoto: 'logbook_photo',
  document: 'document',
}

/**
 * The server's own rules, restated so the picker can enforce them before a byte
 * is sent rather than after two round trips.
 *
 * This is a COPY of `PURPOSE_RULES` in the backend's `core/files/storage.py`,
 * and the server remains the authority: it re-checks the size and reads the
 * file's actual magic bytes at confirm-time, because a declared content type is
 * only ever a claim. What this buys is the fast, local refusal — telling
 * somebody their 8MB photo is too large without first uploading 8MB of it.
 */
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp']

export const PURPOSE_RULES = {
  [FILE_PURPOSES.accountLogo]: { types: IMAGE_TYPES, maxBytes: 2 * 1024 * 1024 },
  [FILE_PURPOSES.userAvatar]: { types: IMAGE_TYPES, maxBytes: 2 * 1024 * 1024 },
  [FILE_PURPOSES.logbookPhoto]: { types: IMAGE_TYPES, maxBytes: 5 * 1024 * 1024 },
  [FILE_PURPOSES.document]: { types: ['application/pdf'], maxBytes: 20 * 1024 * 1024 },
}

/** The `accept` attribute for a picker, so it offers only what will be taken. */
export const acceptFor = (purpose) => PURPOSE_RULES[purpose].types.join(',')

/**
 * Check a file against its purpose before uploading it.
 *
 * Returns null when it passes, or `{ reason, limit }` naming what is wrong —
 * a key rather than a sentence, because the copy belongs to the i18n layer and
 * this module has no business writing what a person reads.
 */
export function checkFile(file, purpose) {
  const rules = PURPOSE_RULES[purpose]
  if (!rules) throw new Error(`unknown file purpose: ${purpose}`)

  // `file.type` is the browser's guess from the extension and is empty often
  // enough to matter. Refused here rather than sent, since the presign needs a
  // content type and a wrong one fails the magic-byte check at step 3 anyway.
  if (!file.type || !rules.types.includes(file.type)) {
    return { reason: 'type', limit: rules.types.join(', ') }
  }
  if (file.size > rules.maxBytes) {
    return { reason: 'size', limit: Math.round(rules.maxBytes / (1024 * 1024)) }
  }
  return null
}

/**
 * Run the whole handshake and return a confirmed `file_id`.
 *
 * @param {File} file
 * @param {string} purpose  One of FILE_PURPOSES.
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<string>} The confirmed file id, ready to attach.
 */
export async function uploadFile(file, purpose, { signal } = {}) {
  const problem = checkFile(file, purpose)
  // Thrown as an ApiError so a caller's existing error handling covers it —
  // a local refusal and a server refusal should not need two shapes.
  if (problem) {
    throw new ApiError({
      code: ERROR_CODES.VALIDATION,
      detail: `file rejected locally: ${problem.reason}`,
      fields: { file: problem.reason },
    })
  }

  /* 1 ─ the permission slip. */
  const ticket = await post(
    endpoints.files.uploadUrl,
    { purpose, content_type: file.type },
    { signal },
  )

  /* 2 ─ the bytes, straight to storage. See the header for why `fetch`.
         `ticket.headers` is sent verbatim: the Content-Type is part of what was
         signed, so changing or adding to it invalidates the signature. */
  let response
  try {
    response = await fetch(ticket.upload_url, {
      method: ticket.method ?? 'PUT',
      headers: ticket.headers ?? {},
      body: file,
      signal,
    })
  } catch (cause) {
    // A network failure, or an abort. Storage is a different origin from the
    // API, so this is also what a missing CORS rule on the bucket looks like.
    if (signal?.aborted) {
      throw new ApiError({ code: ERROR_CODES.CANCELLED, detail: 'upload cancelled' })
    }
    throw new ApiError({
      code: ERROR_CODES.NETWORK,
      detail: `upload could not reach storage: ${cause?.message ?? 'unknown'}`,
    })
  }

  if (!response.ok) {
    // Storage answers XML, not the API's envelope, and its wording is not
    // translatable or meant for a reader. The status is what is worth keeping.
    throw new ApiError({
      code: ERROR_CODES.SERVER,
      detail: `storage refused the upload (${response.status})`,
      status: response.status,
    })
  }

  /* 3 ─ ask the server to inspect what landed. */
  const result = await post(endpoints.files.confirm(ticket.file_id), undefined, { signal })

  /**
   * A REJECTED FILE ANSWERS 200.
   *
   * The server deletes the object and marks the record rejected, then reports
   * that in the body rather than through the status code — so nothing above
   * this line has failed, and a caller checking only for a thrown error would
   * carry on holding a file id that no longer exists. Turned into a throw here,
   * once, so no call site has to remember.
   */
  if (result?.status !== 'confirmed') {
    throw new ApiError({
      code: ERROR_CODES.VALIDATION,
      detail: `file rejected by the server: ${result?.reason ?? 'no reason given'}`,
      fields: { file: 'rejected' },
    })
  }

  return result.file_id
}
